import React from "react";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import { useFormikContext } from "formik";
import Pill, { MIN_PILL_CLASSES } from "components/Pill";
import { COLORS } from "utils/styles";
import { STATUS, UI_THEME } from "utils/constants/ui";
import { getIconForUID } from "components/FormerEditor/utils";
import { scaffoldCombinations } from "utils/combination";
import { NO_OP } from "utils/action";
import classnames from "classnames";
import { STATE_KEYS } from "utils/constants/state";
import {
  COMBINATION_FIELDS,
  FIELD_TYPES,
  FIELD_TYPE_LABEL_MAP
} from "utils/constants/form";
import { setEditorFocus } from "utils/builder";
import { getMenuIntentFocus } from "utils/hooks/focus";
import { FORM_INTENT } from "utils/constants/pricing";

const CheckmarkIcon = getIconForUID("checkmarkCircle");

const FIELD_COPY = {
  NO_OPTIONS: "No options",
  INELIGIBLE: "Ineligible"
};

const FieldsPicker = (props) => {
  const { name } = props;
  const {
    values,
    touched,
    setValues,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const configFieldsPath = `${name}.${STATE_KEYS.FORM.CONFIG_LOGIC_CHECKOUTS_FIELDS}`;
  const selectedFields = get(values, configFieldsPath);

  const fieldGroupsPath = `${name}.${STATE_KEYS.FORM.FIELD_GROUPS}`;
  const fieldGroups = get(values, fieldGroupsPath, []);

  /**
   * TODO: add a warning if any combinations currently exist that this will reset them
   */
  const configCombinationsPath = `${name}.${STATE_KEYS.FORM.CONFIG_LOGIC_CHECKOUTS_COMBOS}`;

  return fieldGroups.map((fieldGroup, fieldGroupIx) => {
    const fields = fieldGroup && fieldGroup.fields ? fieldGroup.fields : [];

    const nodes = fields.map((field, fieldIx) => {
      /**
       * Some fields will be ineligible for combinations (.e.g. textarea / input)
       */
      const isComboField = COMBINATION_FIELDS[field.uid];
      const setFocusToField = isComboField
        ? setEditorFocus({
            values,
            setValues,
            focus: getMenuIntentFocus({
              menuIntent: FORM_INTENT.FIELD_GROUP_ORDER,
              activeIndex: fieldGroupIx,
              activeSubIndex: fieldIx
            })
          })
        : NO_OP;

      const selectedIndex = selectedFields.indexOf(field.uuid);
      const isSelected = selectedIndex > -1;

      const statusColor = isSelected ? COLORS.blue : COLORS.secondary;

      const addFieldToCombinations = () => {
        const updatedSelections = cloneDeep(selectedFields);

        /**
         * Add or remove selected fields
         */
        if (isSelected) {
          updatedSelections.splice(selectedIndex, 1);
        } else {
          updatedSelections.push(field.uuid);
        }
        setFieldValue(configFieldsPath, updatedSelections);

        /**
         * Re-scaffold combinations
         */
        const combos = updatedSelections.length
          ? scaffoldCombinations(updatedSelections, fieldGroups)
          : [];

        setFieldValue(configCombinationsPath, combos);
        const isTouched = get(touched, configCombinationsPath);
        if (!isTouched) {
          setFieldTouched(configCombinationsPath, true);
        }
      };

      let disabled = false;
      let disabledReason;
      let action;
      if (field.uid === FIELD_TYPES.SELECT && !field.options.length) {
        disabled = true;
        disabledReason = FIELD_COPY.NO_OPTIONS;
      }
      let pillProps;
      if (disabled && disabledReason) {
        pillProps = {
          customClasses: MIN_PILL_CLASSES,
          copy: disabledReason,
          status: STATUS.WARNING,
          theme: UI_THEME.SLIM,
          onClick: setFocusToField
        };
      } else if (!isComboField) {
        pillProps = {
          customClasses: MIN_PILL_CLASSES,
          copy: FIELD_COPY.INELIGIBLE,
          theme: UI_THEME.SLIM
        };
      }
      if (pillProps) {
        action = <Pill {...pillProps} />;
      } else {
        action = (
          <div className="dib pointer" onClick={addFieldToCombinations}>
            <CheckmarkIcon color={statusColor} size={20} />
          </div>
        );
      }

      return (
        <div
          key={field.uuid}
          className="w-100 pa3 flex flex-row justify-between"
        >
          <div
            className={classnames("f5 pr2", {
              "o-60": !isComboField,
              "fw5": isComboField
            })}
          >
            <div
              className={classnames("dib", {
                pointer: isComboField
              })}
              onClick={setFocusToField}
            >
              {field.label || FIELD_TYPE_LABEL_MAP[field.uid]}
            </div>
          </div>
          {action && <div className="dib flex-shrink-0">{action}</div>}
        </div>
      );
    });
    const groupLabel = fieldGroup.label || `Field group ${fieldGroupIx + 1}`;

    return (
      <div
        key={fieldGroup.uuid}
        className={classnames(
          "flex flex-column w-100 pt2 ba br2 hairline-1 relative",
          {
            mt4: fieldGroupIx
          }
        )}
      >
        <div className="absolute" style={{ top: -22, left: 8 }}>
          <Pill
            copy={groupLabel}
            customClasses={{
              copy: "f7 pa1 br2 fw5 flex-shrink-0 items-center"
            }}
            status={STATUS.NEUTRAL}
          />
        </div>
        {nodes}
      </div>
    );
  });
};

export default FieldsPicker;
